/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';

import { searchDate } from 'ui-search/src/utils/dateUtils';

import { FlexDatesTrigger } from './FlexDatesTrigger';
import type { FiltersProps, MonthsOptionsProps } from './FlexDates.types';
import { FlexDatesPopup } from './FlexDatesPopup';

export const MAX_MONTHS_AHEAD = 12;

export type FlexDatesProps = {
    filters: FiltersProps;
    onFiltersChange: (v: FiltersProps) => void;
    isSearch: boolean;
    showClear?: boolean;
    monthsLimit?: number;
};

export function FlexDates({
    filters,
    monthsLimit,
    onFiltersChange,
    isSearch,
    showClear,
}: FlexDatesProps) {
    // Layout
    const [isPortalOpen, setIsPortalOpen] = React.useState<boolean>(false);

    const triggerRef = React.useRef<HTMLButtonElement>(null);

    const monthsOptions: MonthsOptionsProps[] = [
        ...new Array(MAX_MONTHS_AHEAD + 1).keys(),
    ].map((item) => ({
        month: searchDate().add({ months: item }).format('MMMM'),
        monthShort: searchDate().add({ months: item }).format('MMM'),
        year: searchDate().add({ months: item }).format('YYYY'),
        value: searchDate().add({ months: item }).format('MM-YYYY'),
    }));

    return (
        <div
            className={cx('flex-dates-container', {
                'flex-dates-container-search': isSearch,
            })}
            id="flex-dates-container"
        >
            <FlexDatesTrigger
                filters={filters}
                isPortalOpen={isPortalOpen}
                setIsPortalOpen={setIsPortalOpen}
                monthsOptions={monthsOptions}
                onClear={() => {
                    onFiltersChange({});
                }}
                ref={triggerRef}
                showClear={showClear}
            />

            {/* Not rendering FlexDatesPopup when it is closed allows the internal state
            to be reset without having to attempt to sync external state using useEffects */}
            {isPortalOpen ? (
                <FlexDatesPopup
                    setIsPortalOpen={setIsPortalOpen}
                    filters={filters}
                    onFiltersChange={onFiltersChange}
                    monthsLimit={monthsLimit}
                    triggerRef={triggerRef}
                    monthsOptions={monthsOptions}
                />
            ) : null}
        </div>
    );
}
